/**
 * Plantillas de documentos y envíos en lote (PLAN_PLANTILLAS_DOCUMENTOS_RRHH).
 *
 * Una plantilla es un texto con variables (`{{trabajador.rut}}`) que se genera
 * como PDF para cada trabajador elegido; si su tipo de documento exige firma,
 * cada PDF sale con su pedido de firma.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

const BASE = '/hr-documents'

export interface CampoDelEnvio {
  key: string
  label: string
  required: boolean
  multiline: boolean
}

export type EstadoPlantilla = 'draft' | 'active' | 'archived'

export interface PlantillaDocumento {
  id: string
  name: string
  document_type: string
  document_type_name: string
  requires_signature: boolean
  folder_name: string
  content_html: string
  envio_fields: CampoDelEnvio[]
  status: EstadoPlantilla
  last_batch_at: string | null
  /** Lo que el saneo quitó al guardar (un <script>, una imagen externa…). */
  content_warnings: string[]
  created_at: string
  updated_at: string
}

export interface VariableDePlantilla {
  key: string
  label: string
  group: string
  type: 'texto' | 'imagen'
}

export interface Candidato {
  id: string
  name: string
  rut: string
  position: string
  area: string
  cost_center: string
  status: string
  /** `sin_correo` | `correo_en_otra_ficha` | '' — por qué no podría firmar. */
  signature_issue: string
}

export interface FilaDeRevision {
  employee_id: string
  name: string
  rut: string
  can_generate: boolean
  code: string
  message: string
  missing: string[]
}

export interface Revision {
  employees: FilaDeRevision[]
  can_generate: number
  cannot_generate: number
  requires_signature: boolean
}

export type EstadoEnvio = 'queued' | 'running' | 'done' | 'failed'

export interface EnvioDocumentos {
  id: string
  template: string
  template_name: string
  name: string
  envio_values: Record<string, string>
  status: EstadoEnvio
  total: number
  generated: number
  failed: number
  requires_signature: boolean
  created_by_name: string
  created_at: string
  finished_at: string | null
}

export interface ItemDelEnvio {
  id: string
  employee: string
  employee_name: string
  employee_rut: string
  document: string | null
  status: 'pending' | 'generated' | 'failed'
  error_code: string
  error_message: string
  signature_status: '' | 'pending' | 'signed' | 'rejected' | 'cancelled'
}

export interface EnvioDetalle extends EnvioDocumentos {
  items: ItemDelEnvio[]
  signatures: { pending: number; signed: number; rejected: number; cancelled: number }
}

export type PlantillaPayload = Pick<PlantillaDocumento,
  'name' | 'document_type' | 'content_html' | 'envio_fields' | 'status'>

const claves = {
  plantillas: ['document-templates'] as const,
  plantilla: (id: string) => ['document-templates', id] as const,
  variables: ['document-templates', 'variables'] as const,
  candidatos: ['document-templates', 'candidates'] as const,
  envios: ['document-batches'] as const,
  envio: (id: string) => ['document-batches', id] as const,
}

export function usePlantillas(status?: EstadoPlantilla) {
  return useQuery({
    queryKey: [...claves.plantillas, status ?? 'vigentes'],
    queryFn: async () => {
      const { data } = await apiClient.get<PlantillaDocumento[] | { results: PlantillaDocumento[] }>(
        `${BASE}/document-templates/`, { params: status ? { status } : undefined })
      return Array.isArray(data) ? data : data.results
    },
  })
}

export function usePlantilla(id: string | undefined) {
  return useQuery({
    queryKey: claves.plantilla(id ?? ''),
    queryFn: async () => (await apiClient.get<PlantillaDocumento>(`${BASE}/document-templates/${id}/`)).data,
    enabled: !!id,
  })
}

export function useVariablesDePlantilla() {
  return useQuery({
    queryKey: claves.variables,
    queryFn: async () =>
      (await apiClient.get<{ variables: VariableDePlantilla[] }>(`${BASE}/document-templates/variables/`)).data.variables,
    staleTime: 60 * 60 * 1000,
  })
}

export function useCandidatos() {
  return useQuery({
    queryKey: claves.candidatos,
    queryFn: async () =>
      (await apiClient.get<{ employees: Candidato[] }>(`${BASE}/document-templates/candidates/`)).data.employees,
  })
}

export function useGuardarPlantilla(id?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<PlantillaPayload>) => {
      const { data } = id
        ? await apiClient.patch<PlantillaDocumento>(`${BASE}/document-templates/${id}/`, payload)
        : await apiClient.post<PlantillaDocumento>(`${BASE}/document-templates/`, payload)
      return data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: claves.plantillas })
      qc.setQueryData(claves.plantilla(data.id), data)
    },
  })
}

export function useArchivarPlantilla() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`${BASE}/document-templates/${id}/`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.plantillas }),
  })
}

/** La vista previa es un PDF: el `Blob` va al visor de PDF de Clobi (`useVisorPdf`). */
export async function pedirVistaPrevia(
  id: string, body: { employee_id: string; envio_values: Record<string, string>; content_html?: string },
): Promise<Blob> {
  const { data } = await apiClient.post<Blob>(`${BASE}/document-templates/${id}/preview/`, body,
    { responseType: 'blob' })
  return data
}

export function useRevisarEnvio(id: string) {
  return useMutation({
    mutationFn: async (body: { employee_ids: string[]; envio_values: Record<string, string> }) =>
      (await apiClient.post<Revision>(`${BASE}/document-templates/${id}/check/`, body)).data,
  })
}

export function useCrearEnvio() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { template: string; employee_ids: string[]; envio_values: Record<string, string> }) =>
      (await apiClient.post<EnvioDocumentos>(`${BASE}/document-batches/`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: claves.envios })
      qc.invalidateQueries({ queryKey: claves.plantillas })
    },
  })
}

export function useEnvios() {
  return useQuery({
    queryKey: claves.envios,
    queryFn: async () => (await apiClient.get<EnvioDocumentos[]>(`${BASE}/document-batches/`)).data,
  })
}

/** Mientras se genera, se consulta cada 2 s; al terminar, deja de preguntar. */
export function useEnvio(id: string | undefined) {
  return useQuery({
    queryKey: claves.envio(id ?? ''),
    queryFn: async () => (await apiClient.get<EnvioDetalle>(`${BASE}/document-batches/${id}/`)).data,
    enabled: !!id,
    refetchInterval: (q) => {
      const estado = q.state.data?.status
      return estado === 'queued' || estado === 'running' ? 2000 : false
    },
  })
}
